import { useQuery } from '@tanstack/react-query';
import {
  ARRANGEMENT_TYPE_KEYS,
  ARRANGEMENT_TYPES,
  type ArrangementTemplateDto,
  type ArrangementType,
  type TemplateCalendarDto,
  type TemplateDecisionClass,
  type TemplateDecisionListDto,
  type TemplateListDto,
  type TemplateSetDto,
  type TemplateSetListDto,
} from '@pams/shared';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [P7B §2–§4] THE TEMPLATE PAGES' READS — one hook per endpoint, every key from the factory.
 *
 *  ⚠️ A SET AND ITS TEMPLATES ARE READ-ONLY FOR EVER, so these carry no write and nothing here is
 *  invalidated by an arrangement write: `queryKeys.templates` is a top-level namespace, deliberately
 *  outside `arrangements.all`'s blast radius. A DECISION saved invalidates `templates.set(setKey)`,
 *  which is the prefix of the set's summary, its templates and its decision lists together.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */

export const useTemplateSets = () =>
  useQuery({
    queryKey: queryKeys.templates.sets(),
    queryFn: ({ signal }) => api.get<TemplateSetListDto>('/templates/sets', { signal }),
  });

/**
 * ⚠️ [P7B.1 §2] A LIBRARY PANE READS WHAT ITS LAYOUT ALREADY WATCHES, AND DOES NOT READ IT AGAIN. The
 * layout observes the set and the template list for as long as the library is open, so it is the one
 * that keeps them fresh; a pane mounting on every pick would otherwise refetch both once they are 30
 * seconds stale — the 182-row list again on each click. Measured: six reads where four do, in the
 * library's own request census. A pane passes `{ pane: true }`; every other caller is unchanged.
 */
type TemplateReadOptions = {
  readonly pane?: boolean;
  /**
   * [P7C §4 · §3] `false` holds the read back — the library's preview and its setup guide load nothing
   * until they are opened. Every existing caller omits it and reads exactly as before.
   */
  readonly enabled?: boolean;
};
const paneOptions = (options: TemplateReadOptions) => (options.pane === true ? { refetchOnMount: false } : {});
const allowed = (options: TemplateReadOptions): boolean => options.enabled ?? true;

export const useTemplateSet = (setKey: string, options: TemplateReadOptions = {}) =>
  useQuery({
    queryKey: queryKeys.templates.set(setKey),
    queryFn: ({ signal }) => api.get<TemplateSetDto>(`/templates/sets/${setKey}`, { signal }),
    enabled: setKey !== '' && allowed(options),
    ...paneOptions(options),
  });

export const useTemplateList = (setKey: string, options: TemplateReadOptions = {}) =>
  useQuery({
    queryKey: queryKeys.templates.list(setKey),
    queryFn: ({ signal }) => api.get<TemplateListDto>(`/templates/sets/${setKey}/templates`, { signal }),
    enabled: setKey !== '' && allowed(options),
    ...paneOptions(options),
  });

export const useTemplate = (setKey: string, templateId: string) =>
  useQuery({
    queryKey: queryKeys.templates.template(setKey, templateId),
    queryFn: ({ signal }) =>
      api.get<ArrangementTemplateDto>(`/templates/sets/${setKey}/templates/${templateId}`, { signal }),
    enabled: setKey !== '' && templateId !== '',
  });

export const useTemplateDecisions = (
  setKey: string,
  decisionClass: TemplateDecisionClass,
  options: TemplateReadOptions = {},
) =>
  useQuery({
    queryKey: queryKeys.templates.decisions(setKey, decisionClass),
    queryFn: ({ signal }) =>
      api.get<TemplateDecisionListDto>(`/templates/sets/${setKey}/decisions`, {
        query: { class: decisionClass },
        signal,
      }),
    enabled: setKey !== '' && allowed(options),
  });

export const useTemplateCalendar = (setKey: string, year: number, options: TemplateReadOptions = {}) =>
  useQuery({
    queryKey: queryKeys.templates.calendar(setKey, year),
    queryFn: ({ signal }) =>
      api.get<TemplateCalendarDto>(`/templates/sets/${setKey}/calendars/${String(year)}`, { signal }),
    enabled: setKey !== '' && Number.isInteger(year) && allowed(options),
  });

/**
 * ⚠️ THE REGISTERED TYPES A SET CAN CREATE, NEWEST YEAR FIRST — read from the closed registry by the
 * set's EVENT, never from its key or its source year. A run's type is `${event}-${year}` and must be
 * registered; an event with no registered type can be browsed and decided, and creates nothing.
 */
export const typesForEvent = (event: string | undefined): ArrangementType[] =>
  ARRANGEMENT_TYPE_KEYS.filter((key) => ARRANGEMENT_TYPES[key].event === event).sort(
    (a, b) => ARRANGEMENT_TYPES[b].year - ARRANGEMENT_TYPES[a].year,
  );

/** The year a set's pages default to: its newest registered type's, or `null` when none is registered. */
export const defaultYearFor = (event: string | undefined): number | null => {
  const [newest] = typesForEvent(event);
  return newest === undefined ? null : ARRANGEMENT_TYPES[newest].year;
};

/** A comma-separated id list from a URL, de-duplicated, empty pieces dropped. */
export const idsFromParam = (value: string | null): string[] =>
  value === null ? [] : [...new Set(value.split(',').filter((id) => id.trim() !== ''))];
